import React, { useCallback, useEffect, useRef, useState } from "react"

import type { ChartVisualizationProps } from "../../../types/chart"

export const TextVisualization: React.FC<ChartVisualizationProps> = ({
  config,
}) => {
  const text = config.text || ""

  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLParagraphElement>(null)
  const [fontSize, setFontSize] = useState(16)

  const fitText = useCallback(() => {
    const container = containerRef.current
    const textEl = textRef.current
    if (!container || !textEl || !text) {
      return
    }

    const style = getComputedStyle(container)
    const maxWidth =
      container.clientWidth -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight)
    const maxHeight =
      container.clientHeight -
      parseFloat(style.paddingTop) -
      parseFloat(style.paddingBottom)

    let low = 10
    let high = 200

    while (low < high - 1) {
      const mid = Math.floor((low + high) / 2)
      textEl.style.fontSize = `${mid}px`

      if (textEl.scrollWidth > maxWidth || textEl.scrollHeight > maxHeight) {
        high = mid
      } else {
        low = mid
      }
    }

    setFontSize(low)
  }, [text])

  useEffect(() => {
    fitText()

    const observer = new ResizeObserver(fitText)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }
    return () => observer.disconnect()
  }, [fitText])

  if (!text) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-coval-text-muted">
        <p className="text-sm">No text configured</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="flex items-center h-full w-full py-1 pr-20 overflow-hidden justify-start text-left"
      data-testid="dashboard-text-widget-content"
    >
      <p
        ref={textRef}
        className="text-coval-text whitespace-pre-wrap wrap-break-word leading-tight select-none pr-8"
        style={{ fontSize: `${fontSize}px` }}
      >
        {text}
      </p>
    </div>
  )
}
